import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import { formatMoney, formatDate, today, daysAgo, primero, useLocalTable } from '../finanzas-cxp/shared.js';
import { exportTablePdf, buildTablePdf, exportTableExcel, cargarLogosPdf } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';

/**
 * Listado de Abonos -- réplica de `IbuFront/.../Admin/Abonos/ListadoAbonos.jsx` (V1):
 * rango Fecha Inicio/Fin (default últimos 30 días), filtros Cliente y Forma Pago,
 * buscador, orden por columna, 50 por página, fila TOTAL, botón PDF por fila (recibo
 * individual) y PDF/Excel del listado. Solo lectura (V1 no edita ni cancela abonos
 * desde el listado). La captura vive en `AbonosCapturaPage.jsx` ("Nuevo Abono").
 */
const ROUTE = 'abonos';

async function pdfRecibo(a) {
  const doc = new jsPDF();
  // V1 (`PDFOC`): solo el logo Sistemify grande y centrado (67,15,70,20).
  const { sistemify } = await cargarLogosPdf();
  if (sistemify) { try { doc.addImage(sistemify, 'PNG', 67, 15, 70, 20); } catch { /* sin logo */ } }
  doc.setFontSize(16);
  doc.text('___________________', 77, 80);
  doc.text('Firma', 98, 90);
  autoTable(doc, {
    head: [['Fecha', 'Cliente', 'Forma Pago', 'Importe']],
    body: [[a.fecha, a.clientes, a.formaPago, formatMoney(a.importe)]],
    startY: 45,
  });
  doc.save('Abono.pdf');
}

export default function AbonosPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso('abonos:ver');
  const puedeCrear = hasPermiso('abonos:crear');

  const [abonos, setAbonos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFechaInicio, setSelectedFechaInicio] = useState(daysAgo(30));
  const [selectedFechaFin, setSelectedFechaFin] = useState(today());
  const [selectedCliente, setSelectedCliente] = useState('');
  const [formaPago, setFormaPago] = useState('');
  const [mailOpen, setMailOpen] = useState(false);

  useEffect(() => {
    apiClient.get('/clientes', { params: { limit: 500, sortBy: 'nombre_comercial', sortOrder: 'asc' } })
      .then(({ data }) => setClientes(data.data || []))
      .catch(() => setClientes([]));
  }, []);

  const cargar = useCallback(async () => {
    if (!selectedFechaInicio || !selectedFechaFin) return;
    setCargando(true);
    setError(null);
    try {
      const { data } = await apiClient.get(`/${ROUTE}/fechas/${selectedFechaInicio}/${selectedFechaFin}`);
      setAbonos((data.data || []).map((a) => {
        const cli = primero(a.clientes);
        return {
          _id: a._id, numero: a.idAbono, fecha: a.fecha,
          clientes: cli?.nombre_comercial || '—', idClientes: cli?._id,
          formaPago: a.formaPago || '', observaciones: a.observaciones || '', importe: a.importe,
        };
      }));
    } catch (e) {
      setError(e.message);
      setAbonos([]);
    } finally {
      setCargando(false);
    }
  }, [selectedFechaInicio, selectedFechaFin]);

  useEffect(() => { cargar(); }, [cargar]);

  const abonosFiltrados = useMemo(
    () => abonos.filter((a) =>
      (!selectedCliente || a.idClientes === selectedCliente) &&
      (!formaPago || a.formaPago === formaPago)),
    [abonos, selectedCliente, formaPago]
  );

  // V1 busca en cliente, forma de pago, observaciones, importe y número.
  const { search, setSearch, page, setPage, sortBy, sortOrder, ordenarPor, pageRows, filtradas, pagination } = useLocalTable(abonosFiltrados, {
    searchFields: ['clientes', 'formaPago', 'observaciones', 'importe', 'numero'], perPage: 50,
    defaultSortBy: 'numero', defaultSortOrder: 'desc',
  });

  const totalFiltrado = filtradas.reduce((t, r) => t + (Number(r.importe) || 0), 0);
  const ordenadasAsc = [...filtradas].sort((a, b) => Number(a.numero) - Number(b.numero));

  // PDF/Excel de V1: Numero, Fecha, Cliente, Forma Pago, Observaciones, Importe (+ Total).
  const columnasExport = [
    { key: 'numero', label: 'Numero' },
    { key: 'fecha', label: 'Fecha' },
    { key: 'clientes', label: 'Cliente' },
    { key: 'formaPago', label: 'Forma Pago' },
    { key: 'observaciones', label: 'Observaciones' },
    { key: 'importe', label: 'Importe', render: (r) => formatMoney(r.importe) },
  ];

  const opcionesPdf = {
    subtitulo: `${selectedFechaInicio} - ${selectedFechaFin}`, fileName: 'Abonos',
    totalCols: { observaciones: 'Total', importe: formatMoney(totalFiltrado) },
  };

  const columnas = [
    { key: 'numero', label: 'Numero', sortable: true },
    { key: 'fecha', label: 'Fecha', sortable: true, render: (r) => formatDate(r.fecha) },
    { key: 'clientes', label: 'Clientes', sortable: true },
    { key: 'formaPago', label: 'Forma Pago', sortable: true },
    { key: 'observaciones', label: 'Observaciones', sortable: true },
    { key: 'importe', label: 'Importe', sortable: true, className: 'text-right', render: (r) => formatMoney(r.importe) },
    {
      key: '__acciones', label: 'PDF', sortable: false, className: 'text-center',
      render: (row) => (
        <button
          title="PDF" aria-label="PDF" className="p-1.5 rounded-md text-danger-500 hover:bg-slate-100"
          onClick={() => { pdfRecibo(row).catch((e) => Swal.fire({ icon: 'error', title: 'No se pudo generar el PDF', text: e.message })); }}
        >
          <FileText size={15} />
        </button>
      ),
    },
  ];

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Reporte de Pagos</h1>
        {puedeCrear && (
          <Link to="/abonos/nuevo" className="btn-primary">
            + Nuevo Abono
          </Link>
        )}
      </div>

      <div className="flex items-center gap-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="input-base w-64" />
        <button
          type="button" className="btn-outline text-xs px-3 py-1.5"
          onClick={() => exportTablePdf('Listado Abonos', columnasExport, ordenadasAsc, opcionesPdf)}
        >
          PDF
        </button>
        <button type="button" className="btn-outline text-xs px-3 py-1.5" onClick={() => setMailOpen(true)}>
          eMail
        </button>
        <button
          type="button" className="btn-outline text-xs px-3 py-1.5"
          onClick={() => exportTableExcel('Listado Abonos', columnasExport, ordenadasAsc, { fileName: 'ListadoAbonos', sheetName: 'ListadoAbonos' })}
        >
          Excel
        </button>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Fecha Inicio</label>
          <input type="date" value={selectedFechaInicio} onChange={(e) => { setSelectedFechaInicio(e.target.value); setPage(1); }} className="input-base" />
        </div>
        <div>
          <label className="label">Fecha Fin</label>
          <input type="date" value={selectedFechaFin} onChange={(e) => { setSelectedFechaFin(e.target.value); setPage(1); }} className="input-base" />
        </div>
        <div>
          <label className="label">Cliente</label>
          <select value={selectedCliente} onChange={(e) => { setSelectedCliente(e.target.value); setPage(1); }} className="select-base">
            <option value="">Selecciona</option>
            {clientes.map((c) => <option key={c._id} value={c._id}>{c.nombre_comercial}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Forma Pago</label>
          <select value={formaPago} onChange={(e) => { setFormaPago(e.target.value); setPage(1); }} className="select-base">
            <option value="">Selecciona una Forma de Pago</option>
            <option value="Efectivo">Efectivo</option>
            <option value="Transfer">Transfer</option>
          </select>
        </div>
      </div>

      <DataTable
        columns={columnas} data={pageRows} loading={cargando} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor}
        footer={{ observaciones: 'TOTAL', importe: formatMoney(pageRows.reduce((t, r) => t + (Number(r.importe) || 0), 0)) }}
      />
      <Pagination pagination={pagination} page={page} onChange={setPage} />

      <EmailModal
        open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Listado Abonos"
        onEnviar={async (email) => {
          const doc = await buildTablePdf('Listado Abonos', columnasExport, ordenadasAsc, opcionesPdf);
          await enviarCorreoAdjunto({
            subject: 'Listado Abonos', email, fileName: 'ListadoAbonos.pdf', docPdf: doc,
            textoHtml: plantillaCorreoV1('Adjunto encontraras el listado de Abonos.'),
          });
        }}
      />
    </div>
  );
}
